import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getProject, 
  subscribeToActivities, 
  subscribeToTasks,
  deleteActivity,
  deleteTask,
  createTask,
  updateTask
} from '../services/firebaseService';
import AddActivityModal from '../components/AddActivityModal';
import EditActivityModal from '../components/EditActivityModal';
import AddTaskModal from '../components/AddTaskModal';
import EditTaskModal from '../components/EditTaskModal';
import ConfirmModal from '../components/ConfirmModal';
import TaskMessaging from '../components/TaskMessaging';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [searchActivities, setSearchActivities] = useState('');
  const [project, setProject] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [showEditActivityModal, setShowEditActivityModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showTaskMessaging, setShowTaskMessaging] = useState(false);
  const [selectedTaskForMessaging, setSelectedTaskForMessaging] = useState(null);
  const [selectedActivityForMessaging, setSelectedActivityForMessaging] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [activityTasks, setActivityTasks] = useState({});
  const [taskSubscriptions, setTaskSubscriptions] = useState({});

  // Handle URL parameters for navigation from notifications
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const activityId = urlParams.get('activity');
    const taskId = urlParams.get('task');
    const openChat = urlParams.get('chat') === 'true';
    
    console.log('URL params:', { activityId, taskId, openChat });
    console.log('Activities loaded:', activities.length);
    console.log('Activity tasks:', activityTasks);
    
    if (activityId && activities.length > 0) {
      // Find the activity by ID
      const activity = activities.find(a => a.id === activityId);
      
      if (activity) {
        console.log('Found activity:', activity);
        console.log('Setting expanded activity to sno:', activity.sno);
        
        // Use activity.sno for expanding (this matches the UI logic)
        setExpandedActivity(activity.sno);
        
        if (taskId) {
          // Look for task in the activity's tasks using activity.sno as key
          const tasksForActivity = activityTasks[activity.sno];
          console.log('Tasks for activity sno', activity.sno, ':', tasksForActivity);
          
          if (tasksForActivity) {
            const task = tasksForActivity.find(t => t.id === taskId);
            console.log('Found task:', task);
            
            if (task && openChat) {
              console.log('Opening task messaging for:', task.name);
              setSelectedTaskForMessaging(task);
              setSelectedActivityForMessaging(activity);
              setShowTaskMessaging(true);
            }
            // If openChat is false, just expand the activity to show the task
          }
        }
        
        // Clear URL parameters after navigation
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        console.log('Activity not found with ID:', activityId);
      }
    }
  }, [location.search, activities, activityTasks]);

  useEffect(() => {
    if (!id || !currentUser) return;

    const loadProject = async () => {
      try {
        const projectData = await getProject(id);
        setProject(projectData);
      } catch (error) {
        console.error('Error loading project:', error);
        navigate('/dashboard');
      }
    };

    const unsubscribe = subscribeToActivities(id, (activitiesData) => {
      setActivities(activitiesData);
      setLoading(false);
      
      // Clean up existing subscriptions first
      Object.values(taskSubscriptions).forEach(unsubscribe => {
        if (unsubscribe) unsubscribe();
      });
      
      const newSubscriptions = {};
      
      // Subscribe to tasks for each activity
      activitiesData.forEach(activity => {
        const taskUnsubscribe = subscribeToTasks(activity.id, (tasks) => {
          setActivityTasks(prev => ({
            ...prev,
            [activity.sno]: tasks
          }));
        });
        
        newSubscriptions[activity.id] = taskUnsubscribe;
      });
      
      setTaskSubscriptions(newSubscriptions);
    });

    loadProject();

    return () => {
      if (unsubscribe) unsubscribe();
      // Clean up task subscriptions
      Object.values(taskSubscriptions).forEach(unsubscribe => {
        if (unsubscribe) unsubscribe();
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentUser, navigate]);

  const handleActivityAdded = () => {
    // Activities will be updated automatically through the subscription
    setShowAddActivityModal(false);
  };

  const handleAddTask = (activity) => {
    setSelectedActivity(activity);
    setShowAddTaskModal(true);
  };

  const handleTaskAdded = async (taskData) => {
    try {
      const taskToCreate = {
        ...taskData,
        activityId: selectedActivity.id, // Use Firebase activity ID
        projectId: id
      };
      
      await createTask(taskToCreate);
      setShowAddTaskModal(false);
    } catch (error) {
      console.error('Error adding task:', error);
    }
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setShowEditTaskModal(true);
  };

  const handleTaskUpdated = async (updatedTask) => {
    try {
      // Remove the id from the update data to avoid overwriting it
      const { id, ...updateData } = updatedTask;
      await updateTask(id, updateData);
      setShowEditTaskModal(false);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = (task) => {
    setDeleteItem({
      type: 'task',
      id: task.id,
      name: task.name
    });
    setShowConfirmModal(true);
  };

  const handleEditActivity = (activity) => {
    setSelectedActivity(activity);
    setShowEditActivityModal(true);
  };

  const handleDeleteActivity = (activity) => {
    setDeleteItem({
      type: 'activity',
      id: activity.id,
      name: activity.particulars
    });
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteItem.type === 'task') {
        await deleteTask(deleteItem.id);
      } else if (deleteItem.type === 'activity') {
        await deleteActivity(deleteItem.id);
      }
      setShowConfirmModal(false);
      setDeleteItem(null);
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleOpenTaskMessaging = (task, activity) => {
    setSelectedTaskForMessaging(task);
    setSelectedActivityForMessaging(activity);
    setShowTaskMessaging(true);
  };

  // Calculate project stats from all tasks
  const calculateProjectStats = () => {
    let totalTasks = 0;
    let notStarted = 0;
    let inProgress = 0;
    let completed = 0;
    let delayed = 0;

    Object.values(activityTasks).forEach(tasks => {
      tasks.forEach(task => {
        totalTasks++;
        if (task.status === 'Pending') notStarted++;
        else if (task.status === 'In Progress') inProgress++;
        else if (task.status === 'Completed') completed++;
        else if (task.status === 'Delayed') delayed++;
      });
    });

    // No need for sample tasks anymore - only count actual Firebase tasks

    const scheduleProgress = totalTasks > 0 ? Math.round((completed / totalTasks) * 100) : 0;

    return {
      totalTasks,
      notStarted,
      inProgress,
      completed,
      delayed,
      scheduleProgress
    };
  };

  const projectStats = calculateProjectStats();

  if (loading || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <button 
              onClick={() => navigate('/')}
              className="hover:text-blue-600 transition-colors"
            >
              Liasoning IQ
            </button>
            <span>/</span>
            <button 
              onClick={() => navigate('/dashboard')}
              className="hover:text-blue-600 transition-colors"
            >
              All Projects
            </button>
            <span>/</span>
            <span className="text-gray-900 font-medium">{project?.name || 'Loading...'}</span>
          </div>
          <div className="flex items-center space-x-4">
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 bg-gray-50 p-6">
        {/* Stats Cards */}
        <div className="mb-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-gray-900">{projectStats.totalTasks}</div>
            <div className="text-sm text-gray-600">Total Tasks</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-red-600">{projectStats.notStarted}</div>
            <div className="text-sm text-red-600">Not Started</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-orange-600">{projectStats.inProgress}</div>
            <div className="text-sm text-orange-600">In Progress</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-green-600">{projectStats.completed}</div>
            <div className="text-sm text-green-600">Completed</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-red-600">{projectStats.delayed}</div>
            <div className="text-sm text-red-600">Delayed</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-gray-900">{projectStats.scheduleProgress}</div>
            <div className="text-sm text-gray-600">Schedule Progress</div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-gray-900">{project.plannedEndDate}</div>
            <div className="text-sm text-gray-600">Planned End Date</div>
          </div>
        </div>

        {/* Activities Section */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Activities</h2>
              <div className="flex items-center space-x-4">
                {/* Add Activity Button */}
                <button 
                  onClick={() => setShowAddActivityModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Add Activity</span>
                </button>
                
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search Activities..."
                    value={searchActivities}
                    onChange={(e) => setSearchActivities(e.target.value)}
                    className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Activities Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No.</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Particulars</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Planned Start Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Planned End Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actual End Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activities.filter(activity => 
                  activity.particulars?.toLowerCase().includes(searchActivities.toLowerCase()) ||
                  activity.assignedTo?.toLowerCase().includes(searchActivities.toLowerCase()) ||
                  activity.status?.toLowerCase().includes(searchActivities.toLowerCase())
                ).map((activity) => (
                  <React.Fragment key={activity.sno}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <button 
                            className="mr-2 p-1"
                            onClick={() => setExpandedActivity(expandedActivity === activity.sno ? null : activity.sno)}
                          >
                            <svg className={`h-4 w-4 text-gray-400 transform transition-transform ${expandedActivity === activity.sno ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {activity.sno}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-900 mr-2">{activity.particulars}</span>
                          <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {activity.plannedStartDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {activity.plannedEndDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`${
                          activity.actualEndDate 
                            ? activity.actualEndDate <= activity.plannedEndDate 
                              ? 'text-green-600 font-medium' 
                              : 'text-red-600 font-medium'
                            : 'text-gray-500'
                        }`}>
                          {activity.actualEndDate || '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(activity.assignedTo) ? (
                            activity.assignedTo.map((assignee, idx) => (
                              <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {assignee}
                              </span>
                            ))
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {activity.assignedTo}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          activity.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          activity.status === 'In Progress' ? 'bg-orange-100 text-orange-800' :
                          activity.status === 'Delayed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {activity.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEditActivity(activity)}
                          className="text-green-600 hover:text-green-900 mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteActivity(activity)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    {expandedActivity === activity.sno && (
                      <tr>
                        <td colSpan="8" className="px-6 py-0">
                          <div className="bg-gray-50 p-4 border-l-4 border-blue-500">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-medium text-gray-900">Tasks for {activity.particulars}</h4>
                              <button 
                                onClick={() => handleAddTask(activity)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium flex items-center space-x-1"
                              >
                                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                <span>Add Task</span>
                              </button>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Task No.</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Task Name</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                  {/* Display actual tasks for this activity */}
                                  {(activityTasks[activity.sno] || []).length === 0 ? (
                                    <tr>
                                      <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                        No tasks added yet. Click "Add Task" to create the first task.
                                      </td>
                                    </tr>
                                  ) : (activityTasks[activity.sno] || []).map((task) => (
                                    <tr key={task.id} className="hover:bg-gray-50">
                                      <td className="px-4 py-2 text-gray-900">{(activityTasks[activity.sno] || []).indexOf(task) + 1}</td>
                                      <td className="px-4 py-2 text-gray-900">{task.name}</td>
                                      <td className="px-4 py-2 text-gray-600">{task.startDate}</td>
                                      <td className="px-4 py-2 text-gray-600">{task.endDate}</td>
                                      <td className="px-4 py-2">
                                        <div className="flex flex-wrap gap-1">
                                          {Array.isArray(task.assignedTo) ? (
                                            task.assignedTo.map((assignee, idx) => (
                                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {assignee}
                                              </span>
                                            ))
                                          ) : (
                                            task.assignedTo && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {task.assignedTo}
                                              </span>
                                            )
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-4 py-2">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          task.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                          task.status === 'In Progress' ? 'bg-orange-100 text-orange-800' :
                                          task.status === 'Delayed' ? 'bg-red-100 text-red-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {task.status}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2">
                                        <div className="flex items-center space-x-2">
                                          <button
                                            onClick={() => handleOpenTaskMessaging(task, activity)}
                                            className="text-green-600 hover:text-green-900 text-xs px-2 py-1 rounded border border-green-300 hover:bg-green-50"
                                            title="Open task discussion"
                                          >
                                            <svg className="h-3 w-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                            </svg>
                                            Chat
                                          </button>
                                          <button
                                            onClick={() => handleEditTask(task)}
                                            className="text-blue-600 hover:text-blue-900 text-xs"
                                          >
                                            Edit
                                          </button>
                                          <button
                                            onClick={() => handleDeleteTask(task)}
                                            className="text-red-600 hover:text-red-900 text-xs"
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Activity Modal */}
      <AddActivityModal 
        isOpen={showAddActivityModal}
        onClose={() => setShowAddActivityModal(false)}
        projectId={id}
        onActivityAdded={handleActivityAdded}
      />

      {/* Edit Activity Modal */}
      <EditActivityModal 
        isOpen={showEditActivityModal}
        onClose={() => setShowEditActivityModal(false)}
        activity={selectedActivity}
        onActivityUpdated={() => setShowEditActivityModal(false)}
      />

      {/* Add Task Modal */}
      <AddTaskModal 
        isOpen={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        activity={selectedActivity}
        onTaskAdded={handleTaskAdded}
      />

      {/* Edit Task Modal */}
      <EditTaskModal 
        isOpen={showEditTaskModal}
        onClose={() => setShowEditTaskModal(false)}
        task={selectedTask}
        activityName={selectedActivity?.particulars}
        onTaskUpdated={handleTaskUpdated}
      />

      {/* Confirm Delete Modal */}
      <ConfirmModal 
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete ${deleteItem?.type || 'Item'}`}
        message={`Are you sure you want to delete "${deleteItem?.name || 'this item'}"? This action cannot be undone.`}
      />

      {/* Task Messaging Modal */}
      {showTaskMessaging && selectedTaskForMessaging && selectedActivityForMessaging && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <div className="fixed bottom-4 right-4 w-96 h-[600px] bg-white shadow-2xl rounded-lg border pointer-events-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Task Discussion</h3>
                <p className="text-sm text-gray-600">
                  {selectedTaskForMessaging.name} • {selectedActivityForMessaging.particulars} • {project?.name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowTaskMessaging(false);
                  setSelectedTaskForMessaging(null);
                  setSelectedActivityForMessaging(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="h-[calc(100%-80px)]">
              <TaskMessaging 
                task={selectedTaskForMessaging}
                activity={selectedActivityForMessaging}
                project={project}
              />
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default ProjectDetails;
